import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { dashboardPathForRole } from "@/lib/roles";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import PasswordInput from "@/components/ui/password-input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { useBranding, brandingFallback } from "@/lib/branding";
import {
    GraduationCap,
    LayoutDashboard,
    Users as UsersIcon,
    ShieldAlert,
    ArrowLeft,
    Lock,
} from "lucide-react";

// Per-role visual theme. We keep the app palette (slate blue + warm gold) and
// distinguish portals through iconography, copy, ornament, and stacked side
// panel rather than a jarring colour swap.
export const ROLE_THEMES = {
    // Single canonical sign-in surface — accepts any role and routes to
    // the correct portal post-login. Iter34+ removed the role-gating and
    // iter35 collapsed the four themed login URLs into one.
    student: {
        key: "student",
        title: "Sign in",
        subtitle: "Cambridge College Online — single sign-in for students, instructors and staff.",
        headline: "Welcome back to Cambridge.",
        lede: "Sign in with your college email. We'll route you to the right dashboard automatically — whether you're a student, instructor, SME, or administrator.",
        Icon: GraduationCap,
        accent: "bg-accent/15 text-accent",
        ring: "ring-1 ring-accent/25",
        aside: "bg-[linear-gradient(135deg,hsl(215_45%_28%)_0%,hsl(215_55%_22%)_100%)]",
        expectedRole: null,
    },
    instructor: {
        key: "instructor",
        title: "Instructor Portal",
        subtitle: "Manage your programs, students, and curriculum.",
        headline: "Craft the next generation of learning.",
        lede: "Build and publish programs, grade students, and see attendance — the instructor toolkit at your fingertips.",
        Icon: LayoutDashboard,
        accent: "bg-primary/10 text-primary",
        ring: "ring-1 ring-primary/25",
        aside: "bg-[linear-gradient(135deg,hsl(215_45%_28%)_0%,hsl(215_60%_18%)_100%)]",
        expectedRole: "instructor",
    },
    staff: {
        key: "staff",
        title: "Staff Portal",
        subtitle: "Administrative staff access.",
        headline: "Keep the college running smoothly.",
        lede: "Enrol walk-in students, answer contact enquiries, and pull attendance reports — without an admin escalation.",
        Icon: UsersIcon,
        accent: "bg-secondary text-foreground",
        ring: "ring-1 ring-secondary-foreground/15",
        aside: "bg-[linear-gradient(135deg,hsl(38_40%_30%)_0%,hsl(38_55%_22%)_100%)]",
        // Staff accounts in this LMS are admins without elevated privileges in
        // MongoDB — treat them as admin role in the check. If/when a dedicated
        // "staff" role ships, flip this to "staff".
        expectedRole: "admin",
    },
    admin: {
        key: "admin",
        title: "Administrator Portal",
        subtitle: "Full system administration — 2-step verification required.",
        headline: "Custodians of the Cambridge.",
        lede: "Manage programs, users, and branding. Every admin sign-in is protected by two-step verification for your security.",
        Icon: ShieldAlert,
        accent: "bg-destructive/10 text-destructive",
        ring: "ring-1 ring-destructive/25",
        aside: "bg-[linear-gradient(135deg,hsl(0_35%_25%)_0%,hsl(215_55%_18%)_100%)]",
        expectedRole: "admin",
        secure: true,
    },
    sme: {
        key: "sme",
        title: "SME Studio",
        subtitle: "Subject-matter experts authoring the college's programs.",
        headline: "Shape the curriculum.",
        lede: "Spin up new programs end-to-end, then hand them off to the instructor who will teach them.",
        Icon: LayoutDashboard,
        accent: "bg-accent/15 text-accent",
        ring: "ring-1 ring-accent/25",
        aside: "bg-[linear-gradient(135deg,hsl(38_45%_28%)_0%,hsl(38_55%_18%)_100%)]",
        expectedRole: "sme",
    },
};

function formatApiErrorDetail(detail) {
    if (detail == null) return "Something went wrong. Please try again.";
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail))
        return detail
            .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
            .filter(Boolean)
            .join(" ");
    if (detail && typeof detail.msg === "string") return detail.msg;
    return String(detail);
}

export default function RoleLoginPage({ role }) {
    const theme = ROLE_THEMES[role] || ROLE_THEMES.student;
    const { refresh, user, loading } = useAuth();
    const branding = useBranding();
    const navigate = useNavigate();

    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [submitting, setSubmitting] = useState(false);

    // Already authenticated → land them in their portal immediately.
    // This kills the "after login it shows the login page again" UX.
    useEffect(() => {
        if (loading || !user) return;
        if (user.must_change_password) {
            navigate("/account/change-password", { replace: true });
            return;
        }
        navigate(dashboardPathForRole(user.role), { replace: true });
    }, [loading, user, navigate]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (submitting) return;
        setSubmitting(true);
        try {
            const { data } = await api.post("/auth/login", {
                email: email.trim(),
                password,
            });

            // Branch on the response shape ------------------------------------------------
            if (data.requires_2fa_setup) {
                // First-time admin login → go to setup wizard with the setup_token
                sessionStorage.setItem("twofa_setup_token", data.setup_token);
                sessionStorage.setItem("twofa_email", data.email);
                navigate("/account/2fa/setup");
                return;
            }
            if (data.requires_2fa) {
                // Subsequent admin login → go to verify page
                sessionStorage.setItem("twofa_challenge_token", data.challenge_token);
                sessionStorage.setItem("twofa_email", data.email);
                navigate("/account/2fa/verify");
                return;
            }

            // Normal (non-admin) login — accept any role and route to the
            // correct portal. We deliberately do NOT reject mismatched roles
            // here anymore: people share a single /login URL and bouncing
            // them with a "wrong portal" toast was the #1 source of
            // "can't login" complaints. The portal-specific themes stay
            // (cosmetic), but they're advisory, not gating.
            const u = data.user;

            localStorage.setItem("token", data.access_token);
            await refresh();
            if (theme.expectedRole && u.role !== theme.expectedRole) {
                toast.info(
                    `Signed in as ${u.role}. Routing you to your dashboard…`,
                );
            } else {
                toast.success(`Welcome back, ${u.name}`);
            }
            if (u.must_change_password) {
                navigate("/account/change-password", { replace: true });
            } else {
                navigate(dashboardPathForRole(u.role), { replace: true });
            }
        } catch (err) {
            const status = err?.response?.status;
            const detail = formatApiErrorDetail(err?.response?.data?.detail);
            // 401 from /auth/login is the generic "invalid credentials"
            // path. We deliberately keep the backend message generic to
            // avoid leaking which accounts exist, but we can still nudge
            // a first-time user toward the setup-email path *without*
            // confirming their email is registered — the same hint is
            // shown to everyone who fails to log in.
            if (status === 401) {
                toast.error(
                    "Invalid email or password. First-time user? Check your email for a setup link from your administrator.",
                    { duration: 9000 },
                );
            } else {
                toast.error(detail || "Login failed");
            }
        } finally {
            setSubmitting(false);
        }
    };

    const { Icon } = theme;

    return (
        <div
            className="min-h-[80vh] grid lg:grid-cols-[1.05fr,1fr] gap-0 overflow-hidden"
            data-testid={`login-page-${theme.key}`}
            data-role-portal={theme.key}
        >
            {/* Left panel: branded hero */}
            <div
                className={`hidden lg:flex flex-col justify-between p-12 text-white ${theme.aside}`}
            >
                <div className="flex items-center gap-3">
                    <img
                        src={branding.logo_url}
                        alt=""
                        className="h-11 w-11 rounded-full object-contain bg-white/10 p-1" onError={brandingFallback}
                    />
                    <div>
                        <div className="font-display text-xl leading-none">
                            {branding.institution_name}
                        </div>
                        <div className="text-[11px] uppercase tracking-[0.25em] text-white/70 mt-1">
                            eLearning · 16+ years of trusted education
                        </div>
                    </div>
                </div>

                <div className="max-w-md">
                    <div
                        className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-white/80 mb-4"
                    >
                        <Icon className="h-4 w-4" />
                        {theme.title}
                    </div>
                    <h1 className="font-display text-4xl leading-tight">
                        {theme.headline}
                    </h1>
                    <p className="mt-5 text-white/80 leading-relaxed">{theme.lede}</p>
                </div>

                <div className="text-xs text-white/55 italic">
                    Veritas · Virtus · Visio
                </div>
            </div>

            {/* Right panel: form */}
            <div className="flex items-center justify-center px-6 py-12 bg-background">
                <Card className="w-full max-w-md border-border shadow-lg shadow-primary/5">
                    <CardContent className="p-8">
                        <Link
                            to="/"
                            className="text-xs text-muted-foreground hover:text-primary inline-flex items-center gap-1 mb-5"
                        >
                            <ArrowLeft className="h-3.5 w-3.5" /> Back to homepage
                        </Link>

                        <div
                            className="inline-flex items-center gap-2 text-xs font-medium uppercase tracking-[0.18em] text-muted-foreground mb-3"
                            data-testid={`login-portal-badge-${theme.key}`}
                        >
                            <Icon className="h-3.5 w-3.5" />
                            Cambridge College Online
                        </div>
                        <h2 className="font-display text-2xl text-foreground tracking-tight">
                            Sign in to your account
                        </h2>
                        <p className="text-sm text-muted-foreground mt-1">
                            Students, instructors, SMEs and staff — all use this single sign-in.
                        </p>

                        {theme.secure && (
                            <div
                                className="mt-5 rounded-md border border-primary/20 bg-primary/5 px-3 py-2.5 text-xs text-foreground/80 flex items-start gap-2"
                                data-testid="login-admin-secure-banner"
                            >
                                <Lock className="h-3.5 w-3.5 text-primary mt-0.5 flex-shrink-0" />
                                <span>
                                    You&rsquo;ll be prompted for your authenticator-app code after
                                    your password.
                                </span>
                            </div>
                        )}

                        <form onSubmit={handleSubmit} className="space-y-5 mt-6">
                            <div className="space-y-2">
                                <Label htmlFor="email">Email</Label>
                                <Input
                                    id="email"
                                    type="email"
                                    required
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    placeholder="you@example.com"
                                    autoFocus
                                    data-testid={`login-email-input-${theme.key}`}
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="password">Password</Label>
                                <PasswordInput
                                    id="password"
                                    required
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    placeholder="••••••••"
                                    data-testid={`login-password-input-${theme.key}`}
                                />
                            </div>
                            <Button
                                type="submit"
                                className="w-full bg-primary hover:bg-primary/90"
                                disabled={submitting}
                                data-testid={`login-submit-button-${theme.key}`}
                            >
                                {submitting ? "Signing in…" : "Sign in"}
                            </Button>
                            <div className="text-xs">
                                <Link
                                    to="/forgot-password"
                                    className="text-muted-foreground hover:text-primary"
                                    data-testid={`login-forgot-link-${theme.key}`}
                                >
                                    Forgot your password?
                                </Link>
                            </div>
                        </form>

                        <div className="mt-6 text-sm text-center text-muted-foreground">
                            New to Cambridge?{" "}
                            <Link
                                to="/register"
                                className="text-primary font-medium"
                                data-testid="go-register-link"
                            >
                                Create an account
                            </Link>
                        </div>
                    </CardContent>
                </Card>
            </div>
        </div>
    );
}
